/**
 * Overrides the Hi-layer max bitrate from the SDK's hardcoded 1200 kbps to
 * 1500 kbps.
 *
 * Production telemetry (meeting 5501 baseline) showed VP9 + 1200 kbps Hi
 * resolves to 640×360 in the encoder's rate-distortion tradeoff even when
 * the link has 2 Mbps of headroom. `qualityLimitationReason: "none"`,
 * `qls.bandwidth = 0`, but outbound width pinned at 640. At 1500 kbps the
 * encoder's tradeoff flips toward 1280×720 for typical webcam content with
 * VP9.
 *
 * SDK regime variation
 * --------------------
 * `DefaultSimulcastUplinkPolicy.calculateEncodingParameters` writes the Hi
 * target into a different rid slot depending on participant count:
 *
 *   - **kHi (1–2 publishers)** — the SDK sets `shouldDisableSimulcast=true`
 *     and stuffs the Hi target into the `'mid'` rid slot as a documented
 *     Chromium workaround (see the SDK source comment about transceiver
 *     reset on reconnect). The `'hi'` rid is `active: false`.
 *   - **kHiAndLow (3–4 publishers)** — Hi target is on the `'hi'` rid;
 *     `'mid'` rid is `active: false`.
 *   - **kMidAndLow (≥5 publishers)** — Hi is off; `'hi'` rid inactive,
 *     `'mid'` rid carries the Mid target (600 kbps), `'low'` rid carries
 *     a reduced Low target.
 *
 * To stay regime-agnostic, this subclass matches on the SDK's hardcoded Hi
 * target *value* rather than the rid name. Any active encoding entry whose
 * `maxBitrate` equals `SDK_HI_TARGET_KBPS_HD * 1000` is the Hi-quality
 * output for the current regime, regardless of which rid slot the SDK
 * routed it through. kMidAndLow and bandwidth-degraded modes carry a
 * different (lower) target value and are correctly skipped — we don't lift
 * caps the policy intentionally imposed for those regimes.
 *
 * Why the policy subclass at all
 * ------------------------------
 * The SDK's `setVideoMaxBandwidthKbps` calls `setIdealMaxBandwidthKbps` on
 * the active policy, which is a no-op on `DefaultSimulcastUplinkPolicy`.
 * Setting `encodings[i].maxBitrate` via `sender.setParameters()` doesn't
 * survive — `SimulcastTransceiverController.setEncodingParameters` clobbers
 * it on every resubscribe (active-speaker changes, applyPreferenceUpdate
 * calls, reconnects). The policy subclass intercepts before the SDK applies
 * the map, so the patched value is what the transceiver sees from the start
 * and through every resubscribe.
 *
 * Brittleness
 * -----------
 * `SDK_HI_TARGET_KBPS_HD` is named here to make the coupling explicit. If a
 * future SDK bump changes the hardcoded Hi target, the value match silently
 * no-ops (we'd notice in production telemetry — `outbound_video_width` would
 * shift). An SDK raise to ≥ our `HI_KBPS` would mean we don't need this
 * patch anyway. An SDK lower would be a regression we'd want visible.
 *
 * Verified against amazon-chime-sdk-js v3.27.x.
 */

import { DefaultSimulcastUplinkPolicy } from "amazon-chime-sdk-js";

const HI_KBPS = 1500;

/**
 * Matches `DefaultSimulcastUplinkPolicy.kHiTargetBitrateKbpsHd` in the SDK
 * source. If the SDK changes this constant in a future version, the
 * value-match in `chooseEncodingParameters` will no longer fire and the
 * patch becomes a silent no-op — surface in production telemetry, not a
 * runtime error.
 */
const SDK_HI_TARGET_KBPS_HD = 1200;

export class HiBitrate1500SimulcastUplinkPolicy extends DefaultSimulcastUplinkPolicy {
  chooseEncodingParameters(): Map<string, RTCRtpEncodingParameters> {
    const params = super.chooseEncodingParameters();
    for (const encoding of params.values()) {
      if (encoding?.active && encoding.maxBitrate === SDK_HI_TARGET_KBPS_HD * 1000) {
        encoding.maxBitrate = HI_KBPS * 1000;
      }
    }
    return params;
  }
}
